import React, { useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  Menu,
  MenuItem,
  Grid,
} from "@mui/material";
import {
  BodyLarge,
  BodyMedium,
  BodySmall,
  H7TitleLarge,
  H9TitleSmall,
  LabelLarge,
  LabelMedium,
  LabelSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  FaMicrochip,
  FaBolt,
  FaHashtag,
  FaProjectDiagram,
} from "react-icons/fa";
import {
  MdSpeed,
  MdMemory,
  MdDeveloperBoard,
  MdDataUsage,
} from "react-icons/md";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BoltIcon from "@mui/icons-material/Bolt";
import GridViewIcon from "@mui/icons-material/GridView";
import LoadingSkeleton from "../Skeleton/PriceConfigurationLoadingSkeleton/LoadingSkeleton.js";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import Styled from "@emotion/styled";
import usePoolsPageContext from "../../context/usePriceConfigurationContext.js";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import usePriceConfigurationContext from "../../context/usePriceConfigurationContext.js";
import ActionDialog from "../../../components/ActionDialog/ActionDialog.js";
import AuradineDataNotFoundState from "../../../components/StyledComponents/EmptyStates/AuradineDataNotFoundState.tsx";
import AuradineErrorState from "../../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { usePostDeletePriceConfig } from "../../queries/usePostDeletePriceConfig.ts";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import { StyledMenu, StyledMenuItem } from "../../../components/StyledComponents/Menu/Menu.tsx";

export const PriceConfigurationCardMenu = (props) => {
  const {
    anchorEl,
    handleMenuClose,
    handleMenuItemClick,
    index,
  } = props;
  const { colors } = useTheme();
  return (
    <StyledMenu
      colors={colors}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      sx={{
        "& .MuiPaper-root": {
          boxShadow:
            "0.4px 0.4px 0.9px rgba(0, 0, 0, 0.12)", // Lightest box shadow
        },
      }}
    >
      <StyledMenuItem
        colors={colors}
        onClick={() => handleMenuItemClick("edit", index)}
      >
        Edit
      </StyledMenuItem>
      <StyledMenuItem
        colors={colors}
        onClick={() => handleMenuItemClick("delete", index)}
      >
        Delete
      </StyledMenuItem>
    </StyledMenu>
  );
};

const PriceConfigurationCard = (props) => {
  const { colors } = useTheme();
  const {
    isFetching,
    isLoading,
    isFetched,
    isError,
    isRefetching,
    refetch,
    error,
    status,
    userRole,
  } = props;
  const {
    priceConfigData: pcData,
    setPriceConfigData,
    setSiteConfigurationDataToEdit,
    setEditConfigModeEnabled,
    setOpenAddPriceConfigModal,
    deletionDialogOpen,
    setDeletionDialogOpen,
    setDeleteDialogHeaderMessage,
    setDeleteDialogBodyMessage,
    deleteDialogBodyMessage,
    deleteDialogHeaderMessage,
    siteConfigurationDataToEdit,
  } = usePriceConfigurationContext();

  const { setToastOpen, setToastMessage } =
    useAppContextProvider();

  const handleDeletionDialogClose = () => {
    setDeletionDialogOpen(false);
  };

  const { mutate: deletePriceConfig } =
    usePostDeletePriceConfig();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedIndex, setSelectedIndex] =
    React.useState(0);

  const handleConfigurationDeletion = () => {
    const configToBeDeleted =
      pcData[selectedIndex].priceConfigName;
    const config_type = "priceConfig";
    deletePriceConfig(
      {
        config_name: configToBeDeleted,
        config_type: config_type,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            setToastOpen(true);
            setToastMessage(
              "Price configuration deleted successfully"
            );
            await refetch();
            setDeletionDialogOpen(false);
          } else {
            setToastOpen(true);
            setToastMessage(response.msg);
            setDeletionDialogOpen(false);
          }
        },
        onError: (error) => {
          setToastOpen(true);
          setToastMessage(
            "Error deleting price configuration,Error:" +
              error
          );
          setDeletionDialogOpen(false);
        },
      }
    );
  };

  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (key, index) => {
    if (key === "edit") {
      console.log(pcData[selectedIndex]);
      setEditConfigModeEnabled(true);
      setSiteConfigurationDataToEdit(pcData[selectedIndex]);
      setOpenAddPriceConfigModal(true);
    } else if (key === "delete") {
      setDeletionDialogOpen(true);
      setSiteConfigurationDataToEdit(pcData[selectedIndex]);
      setDeleteDialogHeaderMessage(
        `Delete ${pcData[selectedIndex].priceConfigName}`
      );
      setDeleteDialogBodyMessage(
        "Are you sure you want to delete this configuration?"
      );
    }
    handleMenuClose();
  };

  return (
    <>
      <List
        dense
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          scrollbarWidth: "thin",
          scrollBehavior: "smooth",
        }}
      >
        {(!isLoading || !isFetching) &&
          isFetched &&
          (isError || status !== "ok") && (
            <AuradineErrorState message="An error occurred while fetching data" />
          )}
        {(isLoading || isFetching) && <LoadingSkeleton />}
        {isFetched &&
          (!isLoading || !isFetching) &&
          status === "ok" &&
          pcData &&
          pcData?.length === 0 && (
            <AuradineDataNotFoundState message="No price configurations found" />
          )}
        {isFetched &&
          (!isLoading || !isFetching) &&
          status === "ok" &&
          pcData &&
          [...pcData].map((data, index) => (
            <React.Fragment key={index}>
              <ListItem
                key={index}
                secondaryAction={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.62rem",
                    }}
                  >
                    {data?.siteList?.length > 0 && (
                      <LabelSmall style={{ color: colors?.text?.hint }}>
                        {data.siteList.length}
                      </LabelSmall>
                    )}
                    <StyledIconButton 
                      colors={colors}
                      disabled={userRole === "user"}
                      onClick={(e) =>
                        handleMenuOpen(e, index)
                      }
                    >
                      <MoreVertIcon />
                    </StyledIconButton>
                  </Box>
                }
                sx={{
                  padding: " 0.75rem 1rem",
                  borderBottom: "1px solid #C4C5D6",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"flex-end"}
                    gap={2}
                  >
                    <BodyMedium
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {data?.priceConfigName}
                    </BodyMedium>
                    <BodyMedium>{`${data?.ISO} • ${data?.PPAType}`}</BodyMedium>
                    {data?.ISO === "ERCOT" &&
                      data?.PPAType === "Index" &&
                      data?.PrizeZone !== "" && (
                        <BodyMedium>
                          {data?.PriceZone}
                        </BodyMedium>
                      )}
                    <BodyMedium>
                      {data?.TunePreference}
                    </BodyMedium>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"flex-end"}
                    gap={4}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"center"}
                      gap="0.25rem"
                    >
                      <BoltIcon
                        fontSize={"extra-small"}
                        sx={{ color: colors?.text?.hint, ml: -1 }}
                      />
                      <BodyMedium
                        sx={{ color: colors?.text?.hint }}
                      >{`$${data?.EnergyPrice}`}</BodyMedium>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"center"}
                      gap="0.25rem"
                    >
                      <BodyMedium sx={{ color: colors?.text?.hint }}>
                        ₿
                      </BodyMedium>
                      <BodyMedium
                        sx={{ color: colors?.text?.hint }}
                      >{`$${data?.HashPrice}`}</BodyMedium>
                    </Box>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"flex-end"}
                    gap={2}
                    width="90%"
                  >
                    {data?.siteList?.length > 0 && (
                      <BodySmall sx={{ color: colors?.text?.hint }}>
                        {data?.siteList?.join(" | ")}
                      </BodySmall>
                    )}
                  </Box>
                </Box>
              </ListItem>
              <PriceConfigurationCardMenu
                {...{
                  anchorEl,
                  handleMenuClose,
                  handleMenuItemClick,
                  index,
                }}
              />
            </React.Fragment>
          ))}
      </List>
      <ActionDialog
        open={deletionDialogOpen}
        onClose={handleDeletionDialogClose}
        headerMessage={deleteDialogHeaderMessage}
        subMessage={deleteDialogBodyMessage}
        buttonText={"Delete"}
        deleteAction={handleConfigurationDeletion}
      />
    </>
  );
};

export default PriceConfigurationCard;
